import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useLobbySettings } from '../../providers/LobbySettingsProvider';
import { useTheme } from '../../providers/ThemeProvider';
import Styles from './About.module.css';
import { Image, Label } from '@app/engine';
// Hidden with the Leadership section below (re-enable together): import { FormatLocalized } from '@shared/common';
import { useMemo } from 'react';

/** Local UI constants for the company tab. */
const localConstants = {
  // Separator rendered between a meta label and its value (e.g. "Headquarters: ").
  labelSeparator: ': '
};

// The Leadership section (founder panels) is intentionally hidden on the Company page — see the commented block in CompanyTab below. Re-enable by
// uncommenting this component, the FormatLocalized import above, and the JSX block.
// /** Props for a single founder card. */
// interface IFounderCardProps {
//   name: string;
//   title: string;
//   bio: string;
//   linkedIn: string;
//   imageAsset: string;
// }
//
// /**
//  * Single founder panel with photo, name, title, bio, and LinkedIn link.
//  *
//  * @param props - Component props.
//  * @returns The founder card element.
//  */
// function FounderCard(props: IFounderCardProps) {
//   const { getThemedAssetDTO } = useTheme();
//   const photoAssetDTO = useMemo(() => getThemedAssetDTO(Constants.Bundle.Lazy, props.imageAsset), [getThemedAssetDTO, props.imageAsset]);
//
//   return (
//     <div className={`${Styles.bubblePanel} ${Styles.founderCard}`}>
//       <div className={Styles.founderHeader}>
//         <Image title={props.name} assetDTO={photoAssetDTO} classNameContainer={Styles.founderPhoto} classNameImageEnabled={Styles.founderPhotoImg} />
//         <div className={Styles.founderNameBlock}>
//           <div className={Styles.founderName}>{props.name}</div>
//           <div className={Styles.founderTitle}>{props.title}</div>
//         </div>
//       </div>
//       <Label text={props.bio} classNameContainer={Styles.founderBio} />
//       <a href={props.linkedIn} target="_blank" rel="noopener noreferrer" className={Styles.founderLink}>
//         {Localized.AboutLinkedInLabel} →
//       </a>
//     </div>
//   );
// }

/**
 * Company tab — SKAR AI info, headquarters, contact, and founder panels.
 *
 * @returns The company tab element.
 */
export function CompanyTab() {
  const { getThemedAssetDTO } = useTheme();
  const { contactEmail } = useLobbySettings();
  const logoAssetDTO = useMemo(() => getThemedAssetDTO(Constants.Bundle.Lazy, 'CompanyLogo.png'), [getThemedAssetDTO]);

  return (
    <section>
      <Label text={Localized.AboutCompanyLabel} classNameContainer={Styles.sectionLabel} />
      <Label text={Localized.AboutCompanyTitle} classNameContainer={Styles.sectionTitle} />

      <div className={Styles.companyGrid}>
        <div className={`${Styles.bubblePanel} ${Styles.companyInfoRow}`}>
          <Image title={Constants.Name} assetDTO={logoAssetDTO} classNameContainer={Styles.companyLogoWrap} classNameImageEnabled={Styles.companyLogoImg} />
          <div className={Styles.companyDetails}>
            <div className={Styles.companyName}>{Constants.Name}</div>
            <div className={Styles.companyMeta}>
              <div className={Styles.companyMetaItem}>
                <Label classNameContainer={Styles.companyMetaLabel} text={`${Localized.AboutCompanyHQ}${localConstants.labelSeparator}`} />
                <Label text={Localized.AboutCompanyHQValue} />
              </div>
              {contactEmail && (
                <div className={Styles.companyMetaItem}>
                  <Label classNameContainer={Styles.companyMetaLabel} text={`${Localized.AboutCompanyContact}${localConstants.labelSeparator}`} />
                  <Label text={contactEmail} />
                </div>
              )}
            </div>
            <Label text={Localized.AboutCompanyIntro} classNameContainer={Styles.bubblePanelText} />
            <a href={Constants.CompanyLinkedIn} target="_blank" rel="noopener noreferrer" className={`${Styles.founderLink} ${Styles.companyLink}`}>
              {Localized.AboutLinkedInLabel} →
            </a>
          </div>
        </div>

        {/* The Leadership section (two founder panels) is intentionally hidden - the Company info panel above carries the page alone for now. Re-enable by
            uncommenting this block, the FounderCard component, and the FormatLocalized import above. */}
        {/*
        <Label text={Localized.AboutCompanyFoundersLabel} classNameContainer={Styles.sectionLabel} />
        <Label text={Localized.AboutCompanyFoundersTitle} classNameContainer={`${Styles.sectionTitle} ${Styles.foundersTitle}`} />

        <div className={Styles.foundersGrid}>
          <FounderCard
            name={Constants.FounderCTOName}
            title={Localized.AboutFounderCTOTitle}
            bio={FormatLocalized(Localized.AboutFounderCTOBio, Constants.FounderCTOFirstName)}
            linkedIn={Constants.FounderCTOLinkedIn}
            imageAsset="FounderCTO.png"
          />
          <FounderCard
            name={Constants.FounderCPOName}
            title={Localized.AboutFounderCPOTitle}
            bio={FormatLocalized(Localized.AboutFounderCPOBio, Constants.FounderCPOFirstName)}
            linkedIn={Constants.FounderCPOLinkedIn}
            imageAsset="FounderCPO.png"
          />
        </div>
        */}
      </div>
    </section>
  );
}
